import { Component } from "react";

import { MainSection as FourOhFourSection } from "@/templates/404/404-page/src/sections/main";

import {
    isContentNotFoundError,
    isContentFetchError,
    isRelayError
} from "../common/errors";

type ContentNotFoundErrorBoundaryProps = {
    fallback?: React.ReactNode;
    children: React.ReactNode;
};

const shouldShowFallback = (error: unknown): boolean => {
    return (
        isContentNotFoundError(error) ||
        isRelayError(error) ||
        isContentFetchError(error)
    );
};

// This error boundary is used to catch ContentNotFoundError errors and display an optional fallback which defaults to a 404 page
export class ContentNotFoundErrorBoundary extends Component<
    ContentNotFoundErrorBoundaryProps,
    { error?: Error }
> {
    public static readonly defaultProps = {
        fallback: <FourOhFourSection />
    };

    public constructor(props: ContentNotFoundErrorBoundaryProps) {
        super(props);
        this.state = {};
    }

    public static getDerivedStateFromError(error: Error) {
        // Update state so the next render will show the fallback UI.
        return { error };
    }

    public override render() {
        const { fallback, children } = this.props;
        const hasError = this.state.error != null;
        const shouldRenderFallback =
            hasError && shouldShowFallback(this.state.error);

        if (shouldRenderFallback) {
            return fallback;
        } else if (hasError) {
            // Throw the unhandled error.
            throw this.state.error;
        } else {
            return children;
        }
    }
}

export default ContentNotFoundErrorBoundary;
